import type { AuthTokens, AuthUser } from "@/data/types/auth";
import { storageManager } from "@/services/storage/storage/storage";

/**
 * Authentication storage service
 * Uses IndexedDB for token storage (accessible to Service Worker)
 * NOTE: IndexedDB provides same-origin security, so encryption is optional
 */
class AuthStorageService {
  /**
   * Ensure storage manager is initialized
   */
  private async ensureInitialized(): Promise<void> {
    await storageManager.initialize();
  }

  /**
   * Store authentication tokens in IndexedDB
   */
  async setTokens(tokens: AuthTokens): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.setAuthTokens(tokens);
      console.log("🔐 Tokens stored in IndexedDB");
    } catch (error) {
      console.error("Failed to store tokens:", error);
    }
  }

  /**
   * Retrieve authentication tokens from IndexedDB
   */
  async getTokens(): Promise<AuthTokens | null> {
    try {
      await this.ensureInitialized();
      const tokens = await storageManager.getAuthTokens<AuthTokens>();

      if (!tokens) {
        return null;
      }

      // Validate token structure
      if (!tokens.accessToken || !tokens.expiresAt) {
        console.warn("Invalid token structure, clearing tokens");
        await this.clearTokens();
        return null;
      }

      return tokens;
    } catch (error) {
      console.error("Failed to retrieve tokens:", error);
      await this.clearTokens();
      return null;
    }
  }

  /**
   * Store user information in IndexedDB
   */
  async setUser(user: AuthUser): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.setAuthUser(user);
      console.log("👤 User data stored in IndexedDB");
    } catch (error) {
      console.error("Failed to store user data:", error);
    }
  }

  /**
   * Retrieve user information from IndexedDB
   */
  async getUser(): Promise<AuthUser | null> {
    try {
      await this.ensureInitialized();
      const user = await storageManager.getAuthUser<AuthUser>();
      return user;
    } catch (error) {
      console.error("Failed to retrieve user data:", error);
      await this.clearUser();
      return null;
    }
  }

  /**
   * Store OAuth state parameter for CSRF protection in IndexedDB
   */
  async setState(state: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.setAuthState(state);
    } catch (error) {
      console.error("Failed to store auth state:", error);
    }
  }

  /**
   * Retrieve OAuth state parameter from IndexedDB
   */
  async getState(): Promise<string | null> {
    try {
      await this.ensureInitialized();
      return await storageManager.getAuthState();
    } catch (error) {
      console.error("Failed to retrieve auth state:", error);
      return null;
    }
  }

  /**
   * Clear OAuth state parameter from IndexedDB
   */
  async clearState(): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.clearAuthState();
    } catch (error) {
      console.error("Failed to clear auth state:", error);
    }
  }

  /**
   * Clear tokens from IndexedDB
   */
  async clearTokens(): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.clearAuthTokens();
      console.log("🔐 Tokens cleared from IndexedDB");
    } catch (error) {
      console.error("Failed to clear tokens:", error);
    }
  }

  /**
   * Clear user data from IndexedDB
   */
  async clearUser(): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.clearAuthUser();
      console.log("👤 User data cleared from IndexedDB");
    } catch (error) {
      console.error("Failed to clear user data:", error);
    }
  }

  /**
   * Clear all authentication data from IndexedDB
   */
  async clearAll(): Promise<void> {
    try {
      await this.ensureInitialized();
      await storageManager.clearAllAuthData();
      console.log("🧹 All auth data cleared from IndexedDB");
    } catch (error) {
      console.error("Failed to clear all auth data:", error);
    }
  }

  /**
   * Check if tokens are expired or about to expire
   * Uses a 60-second buffer to refresh proactively before actual expiration
   */
  isTokenExpired(tokens: AuthTokens): boolean {
    const now = Math.floor(Date.now() / 1000);
    const bufferSeconds = 60;
    return tokens.expiresAt <= now + bufferSeconds;
  }

  /**
   * Check if user is authenticated (synchronous check for backward compatibility)
   * NOTE: This is async in nature, but we provide sync interface for compatibility
   */
  async isAuthenticated(): Promise<boolean> {
    const tokens = await this.getTokens();
    if (!tokens) {
      return false;
    }
    return !this.isTokenExpired(tokens);
  }
}

// Export singleton instance
export const authStorage = new AuthStorageService();
